'use client';

import placeholder from '@/assets/images/common/logo.gif';
import { useStyle } from '@/classify';
import { EstimateDeliveryNew } from '@/components/EstimateDelivery/estimate-delivery-new';
import FormError from '@/components/FormError';
import Icon from '@/components/Icon';
import Price from '@/components/Price';
import type { ProductRoot } from '@/components/ProductFullDetail-new/types/product';
import ProductImageCarousel from '@/components/ProductImageCarousel';
import { Form } from '@/components/ui/form';
import useIsMobile from '@/hooks/use-is-mobile';
import { useGetFinalPriceAndCrossPrice } from '@/hooks/useGetFinalPriceAndCrossPrice';
import { cn } from '@/lib/utils';
import { isProductConfigurable } from '@/util/isProductConfigurable';
import Image from 'next/image';
import React, {
  Dispatch,
  ForwardedRef,
  forwardRef,
  ReactNode,
  SetStateAction,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { X as CloseIcon } from 'react-feather';
import { UseFormReturn } from 'react-hook-form';
import Balancer from 'react-wrap-balancer';
import { useGetColorNumber } from '../Product/use-get-color-number';
import { Agree } from './components/agree';
import { CartAction } from './components/cart-action';
import { ChangeStyle } from './components/change-style';
import { CustomizedSize } from './components/customized-size';
import { HasCustomizationOptionsDialog } from './components/has-customization-options-dialog';
import { MaybeCode } from './components/maybe-code';
import { MaybeTryOn } from './components/maybe-try-on';
import { MobileAddToCartSuccessTip } from './components/mobile-addtocart-success-tip';
import NotifyMeDialogLazyLoad from './components/notify-me-dialog-lazy-load';
import { PageBuilderAttributes } from './components/page-builder-attributes';
import { QuantitySelect } from './components/quantity-select';
import { ShareWith, ShareWithTriggerMobile } from './components/share-with';
import { StandardOptions } from './components/standard-options';
import SwatchAddToCartDialogLazyLoad from './components/swatch-add-to-cart-dialog-lazy-load';
import { useHasCustomizationsDialog } from './hooks/use-has-customizations-dialog';
import { useIsOutofStock } from './hooks/use-is-out-of-stock';
import { useOptionsSpecs } from './hooks/use-options-sepcs';
import { useProductForm } from './hooks/use-product-form';
import { useShowNotifyMeDialog } from './hooks/use-show-notifyme-dialog';
import { useShowShareWith } from './hooks/use-show-share-with';
import { MaybeTabs } from './maybe-tabs';
import defaultClasses from './productFullDetail.module.css';
import { useProductFullDetailStore } from './store/use-product-full-detail-store';
import type { ShowAttribute } from './types/product';
import { useCheckCustomSize } from '@/hooks/use-check-custom-size';
import WaistTipDialog from '../waist-tip-dialog';
import HeightTipDialog from '../height-tip-dialog';

interface Props {
  product: ProductRoot;
  onViewReviews: () => void;
  urlKey: string;
  setCurrentProduct: (product: 'product' | 'available_product') => void;
  className?: string;
  currentProduct: 'product' | 'available_product';
  instockSku?: string;
  hasAvailableProduct?: boolean;
  productTitle: React.ReactNode;
  productRating?: React.ReactNode;
  productFullAccordion: React.ReactNode;
  wishlistDom: React.ReactNode;
  shouldSelectFirstColor?: boolean;
  currentFabricArrayShowTabs?: string[];
  categoryUrlKey: string;
  createFabricButton?: React.ReactNode;
  show_attribute?: ShowAttribute[];
  outerProduct: ProductRoot;
  categoryName?: string;
  onColorLabelChange?: (colorLabel: string) => void;
  defaultColorLabel?: string;
  searchParamsRecord: Record<string, string | string[] | undefined>;
  upsellProductSlider: ReactNode;
  setRelatedProductFilterColor?: Dispatch<SetStateAction<string>>;
}

export interface RefType {
  handleFabricChange?: (fabricLabel: string) => void;
  form?: UseFormReturn<any, any, undefined>;
}

export const ProductFullDetailNew = forwardRef<RefType, Props>(
  (
    {
      product,
      onViewReviews,
      urlKey,
      setCurrentProduct,
      className,
      currentProduct,
      instockSku,
      hasAvailableProduct,
      productTitle,
      productRating,
      productFullAccordion,
      wishlistDom,
      shouldSelectFirstColor,
      currentFabricArrayShowTabs,
      categoryUrlKey,
      createFabricButton,
      show_attribute,
      outerProduct,
      categoryName,
      onColorLabelChange,
      defaultColorLabel,
      searchParamsRecord,
      upsellProductSlider,
      setRelatedProductFilterColor,
    },
    ref: ForwardedRef<RefType>,
  ) => {
    const classes = useStyle(defaultClasses);

    const isMobile = useIsMobile();
    const isDesktop = !isMobile;

    const { isCustomizationsDialogOpen, setIsCustomizationsDialogOpen } = useHasCustomizationsDialog();

    const isTryOn = useMemo(() => {
      return !!product?.is_try_on;
    }, [product]);

    const showMobileCustomlizeDialog = useProductFullDetailStore(state => state.showMobileCustomlizeDialog);
    const setShowMobileCustomlizeDialog = useProductFullDetailStore(state => state.setShowMobileCustomlizeDialog);

    const {
      handleWaistTipDialogCancel,
      handleWaistTipDialogConfirm,
      handleHeightTipDialogCancel,
      handleHeightTipDialogConfirm,
      prevWaistConfirmedRef,
      prevHeightConfirmedRef,
      showWaistTipDialog,
      showHeightTipDialog,
      confirmCustomSize,
    } = useCheckCustomSize({ product, setShowMobileCustomSizeDialog: setShowMobileCustomlizeDialog });

    const options = useProductFullDetailStore(state => state.getOptions(product));
    const {
      form,
      onSubmit,
      hasOnlyOneColor,
      optionSelections,
      mediaGalleryEntries,
      isAdding,
      addToCartStatus,
      quantity,
      errorMessage,
      isValid,
      errors,
      handleFabricChange,
    } = useProductForm({
      options,
      product,
      isTryOn,
      instockSku,
      shouldSelectFirstColor,
      defaultColorLabel,
      searchParamsRecord,
      clearCheckCustomSizeRecord: () => {
        prevWaistConfirmedRef.current = false;
        prevHeightConfirmedRef.current = false;
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        handleFabricChange,
        form,
      }),
      [handleFabricChange, form],
    );

    const hasCusSize = useMemo(() => {
      if (isProductConfigurable(product)) {
        return false;
      }

      const sizeOptionValues = options.find(option => option.label === 'Size')?.values;

      return sizeOptionValues && sizeOptionValues?.some((item: any) => item.label === 'customized_size');
    }, [options, product]);

    const selectedProduct = useProductFullDetailStore(state => state.getSelectedProduct(product, optionSelections));

    // https://www.awbridal.ca/aw-canvas-tote-bag-lpb23077cp.html
    // 有弹窗的商品
    const hasCustomizationOptions = useMemo(() => {
      return product.robes_gifts_customization?.length > 0;
    }, [product.robes_gifts_customization?.length]);

    const { showShareBtn, shareBtn } = useShowShareWith();

    const { optionSpecs, handleClick } = useOptionsSpecs({
      product,
    });

    const [swatchAddToCartDialogVisible, setSwatchAddToCartDialogVisible] = useState(false);

    const { isEverythingOutOfStock } = useIsOutofStock({
      product,
      optionSelections,
    });

    const { notifyMeFormDialogIsOpen, showNotifyMeFormDialog, hiddenNotifyMeFormDialog } = useShowNotifyMeDialog();
    const { finalPrice, crossPrice, hasCrossPrice, currency } = useGetFinalPriceAndCrossPrice({
      product,
      optionSelections,
    });

    const fabric = form.watch('Fabric');

    const fabricLabel = fabric?.label;
    const colorLabel = form.watch('Color')?.label;

    const { colorNumber, configurableColorNumber } = useGetColorNumber({
      product: outerProduct,
      fabricLabel: fabricLabel,
      optionSpecs,
    });

    const handleApplyClick = (...args: any[]) => {
      const colorLabel = form.getValues('Color')?.label;
      const fabricLabel = form.getValues('Fabric')?.label;
      const isDefaultFabric = !fabricLabel || fabricLabel.slice(-8) === '-default';

      onColorLabelChange?.(isDefaultFabric ? colorLabel : '');
      handleClick(...args);
    };

    const currentSwatchUrl = (() => {
      const { order_free_swatch } = product;

      if (!Array.isArray(order_free_swatch)) {
        return null;
      }

      return (
        order_free_swatch.find(x => x?.label?.toLowerCase() === fabricLabel?.replace('-default', '')?.toLowerCase())
          ?.href || null
      );
    })();

    return (
      <>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(data => {
              confirmCustomSize(data).then(confirmed => {
                if (!confirmed) {
                  return;
                }

                onSubmit(data);
              });
            })}
            className={cn('w-full space-y-6', className)}
            suppressHydrationWarning
          >
            <div className='m-center flex max-w-[1440px] flex-col justify-between gap-x-[60px] px-0 lg_flex-row'>
              {isMobile ? (
                <div className='relative bg-white px-2.5  pt-[15px]'>
                  {productTitle}
                  <div className='mt-2.5 flex flex-wrap items-center gap-x-[25px]'>
                    <div className='text-lg font-medium leading-[22px]'>
                      <Price
                        currencyCode={currency}
                        value={finalPrice}
                      />
                      <span className={cn('text-sm line-through', 'ml-2.5 text-[#ABA6A0]')}>
                        {hasCrossPrice ? (
                          <Price
                            currencyCode={currency}
                            value={crossPrice}
                          />
                        ) : null}
                      </span>
                    </div>
                    {productRating}
                  </div>
                  <ShareWithTriggerMobile showShareBtn={showShareBtn} />
                </div>
              ) : null}

              <section className={classes.imageCarousel}>
                {mediaGalleryEntries ? (
                  <ProductImageCarousel
                    images={mediaGalleryEntries}
                    videoUrl={product.video_url}
                    mark={product.product_mark}
                    name={product.name}
                    sku={product.sku}
                    version={product.version}
                    categoryName={categoryName}
                    fabricLabel={fabricLabel}
                    colorLabel={colorLabel}
                    showSpecialImage
                    shareWith={
                      <ShareWith
                        product={product}
                        shareBtn={shareBtn}
                        showShareBtn={showShareBtn}
                      />
                    }
                  />
                ) : (
                  <div className='lg_pb-5'>
                    <Image
                      width={610}
                      height={859}
                      alt={product.name}
                      src={placeholder.src}
                      className='h-full w-full object-cover'
                    />
                  </div>
                )}

                {upsellProductSlider}
              </section>

              <section className=' mt-2.5 shrink lg_mt-0 lg_basis-[573px]'>
                {!isMobile ? (
                  <section className='relative mt-[20px] flex flex-col px-mbpx sm_px-0 lg_mt-0'>
                    {productTitle}

                    {/** 产品评论 */}
                    {productRating}

                    {/** 产品价格 */}
                    <p className=' flex items-center justify-start text-[26px] font-medium'>
                      <Price
                        currencyCode={currency}
                        value={finalPrice}
                      />
                      <span
                        className={cn(
                          'text-sm font-normal line-through',
                          'ml-2.5 text-[#ABA6A0]',
                          'block h-[30px] leading-[36px]',
                        )}
                      >
                        {hasCrossPrice ? (
                          <Price
                            currencyCode={currency}
                            value={crossPrice}
                          />
                        ) : null}
                      </span>
                    </p>
                  </section>
                ) : null}

                <div className='flex flex-col  gap-y-2.5 lg_mt-[30px] lg_gap-y-[30px]'>
                  <StandardOptions
                    hasOnlyOneColor={hasOnlyOneColor}
                    product={product}
                    handleSelectionChange={() => {}}
                    outOfStockVariants={[]}
                    handleClick={handleApplyClick}
                    optionSpecs={optionSpecs}
                    hasAvailableProduct={!!hasAvailableProduct}
                    isAvailableProduct={currentProduct === 'available_product'}
                    setRelatedProductFilterColor={setRelatedProductFilterColor}
                    maybeTabs={
                      <MaybeTabs
                        hasAvailableProduct={!!hasAvailableProduct}
                        currentFabricArrayShowTabs={currentFabricArrayShowTabs}
                        fabricLabel={fabricLabel}
                        currentProduct={currentProduct}
                        setCurrentProduct={setCurrentProduct}
                        colorNumber={colorNumber}
                        configurableColorNumber={configurableColorNumber}
                        className='mb-2.5 px-2.5 lg_mb-5 lg_px-0'
                      />
                    }
                    categoryUrlKey={categoryUrlKey}
                    createFabricButton={createFabricButton}
                    show_attribute={show_attribute}
                    setSwatchAddToCartDialogVisible={() => {
                      setSwatchAddToCartDialogVisible(true);
                    }}
                    showSwatchAddToCartTrigger={!!currentSwatchUrl}
                  />
                </div>

                {hasCusSize && (
                  <div className='hidden px-[15px] text-center text-base leading-5 lg_block lg_px-0'>
                    CUSTOM SIZE (IT’S FREE NOW!)
                  </div>
                )}

                {isMobile ? (
                  <div
                    className={cn(
                      'fixed left-0 top-0 z-[1000] h-full w-full bg-white',
                      showMobileCustomlizeDialog ? 'block' : 'hidden',
                    )}
                  >
                    <div className='relative h-[38px]'>
                      <button
                        title='close'
                        type='button'
                        className='absolute right-[10px] top-[10px]'
                        onClick={() => setShowMobileCustomlizeDialog(false)}
                      >
                        <Icon src={CloseIcon} />
                      </button>
                    </div>
                    <div className='h-[calc(100%-38px)] overflow-y-auto pb-[90px]'>
                      <div className='text-secondary mx-[38px] mb-[38px] text-center font-CeraPro text-sm'>
                        <Balancer>You can customize size, length, pockets, and more for FREE!</Balancer>
                      </div>
                      <div className=''>
                        <CustomizedSize
                          shippingType={(product?.shipping_type ?? 'Dresses') as 'Dresses' | 'Wedding Dresses'}
                          product={product}
                        />
                        <ChangeStyle product={product} />
                        <Agree />
                      </div>
                    </div>

                    <div className='fixed bottom-0 left-0 w-full bg-white p-[15px] shadow-[3px_0px_2px_2px_rgba(0,0,0,0.1)]'>
                      <button
                        type='submit'
                        className='text-uppercase m-[10px] h-[40px] w-[calc(100%-20px)] bg-primary text-center leading-[40px] text-white'
                        onClick={async e => {
                          const allFormValues = form.getValues();
                          const isFloorLengthSizeDress =
                            product.show_attribute?.find(item => item.code === 'aw_dresses_hemline')?.value ===
                            'Floor Length';
                          const { bust, waist, hip, hollowToFloor, hollowtoHem, height, extraLength, agreement } =
                            allFormValues;
                          const triggerFields = [
                            'bust',
                            'waist',
                            'hip',
                            product?.shipping_type === 'Dresses' ? 'hollowtoHem' : 'hollowToFloor',
                            'height',
                            'agreement',
                          ];

                          if (isFloorLengthSizeDress) {
                            triggerFields.push('extraLength');
                          }

                          const isCustomSizeValid = await form.trigger(triggerFields);

                          if (
                            isCustomSizeValid &&
                            height &&
                            hollowtoHem &&
                            parseFloat(hollowtoHem) > parseFloat(height)
                          ) {
                            form.setError('height', {
                              type: 'custom',
                              message: 'Ensure hollow to hem ≤ height. Please adjust.',
                            });
                            form.setError('hollowtoHem', {
                              type: 'custom',
                              message: 'Ensure hollow to hem ≤ height. Please adjust.',
                            });
                            e.preventDefault();
                            return;
                          }

                          if (isCustomSizeValid) {
                            const confirmed = await confirmCustomSize(allFormValues);

                            if (!confirmed) {
                              return;
                            }
                          }

                          if (
                            bust &&
                            waist &&
                            hip &&
                            (product?.shipping_type === 'Dresses' ? hollowtoHem : hollowToFloor) &&
                            height &&
                            (isFloorLengthSizeDress ? extraLength : true) &&
                            agreement &&
                            isCustomSizeValid
                          ) {
                            setShowMobileCustomlizeDialog(false);
                          }
                        }}
                      >
                        DONE
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <CustomizedSize
                      shippingType={(product?.shipping_type ?? 'Dresses') as 'Dresses' | 'Wedding Dresses'}
                      product={product}
                    />
                    <ChangeStyle product={product} />
                    <Agree />
                  </>
                )}

                <MaybeTryOn isTryOn={isTryOn} />

                {/*    {estimateDelivery?.length > 0 ? (
                            <EstimateDelivery
                                className={classes.estimatedelivery}
                                categories={product.categories}
                                sizeChartBlock={product.show_attribute}
                                estimateDelivery={estimateDelivery[0]}
                                categoryIdArray={product.categories}
                                isWeddingShoes={isWeddingShoes}
                                productSku={product.sku}
                            />
                        ) : null} */}

                <EstimateDeliveryNew
                  estimate_delivery={product?.estimate_delivery}
                  sku={product.sku}
                />

                <MaybeCode product={product} />

                <div className='bg-white py-[15px] text-center lg_hidden '>{wishlistDom}</div>

                <div className='fixed bottom-0 z-[49] flex w-full flex-col items-end justify-between bg-white p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1),0_-2px_4px_-2px_rgba(0,0,0,0.1)] lg_relative lg_z-auto lg_flex-col lg_p-0 lg_shadow-none'>
                  {isMobile && (
                    <FormError
                      classes={{
                        root: 'my-0 px-0 text-center mb-2 ',
                      }}
                      errors={[]}
                      message={errorMessage?.message || errorMessage?.toString()}
                    />
                  )}

                  <div className='flex w-[calc(100%-69px)] flex-row items-center justify-end lg_w-full lg_flex-col lg_items-stretch'>
                    <QuantitySelect
                      hasCustomizationOptions={hasCustomizationOptions}
                      maybeWishList={<div className='hidden items-center lg_flex'>{wishlistDom}</div>}
                    />

                    {isDesktop && (
                      <FormError
                        classes={{
                          root: classes.formErrors,
                        }}
                        errors={[]}
                        message={errorMessage?.message || errorMessage?.toString()}
                      />
                    )}

                    <div className='lg_mt-[15px]'>
                      <CartAction
                        product={product}
                        isEverythingOutOfStock={isEverythingOutOfStock}
                        addToCartStatus={addToCartStatus}
                        isAddingProduct={isAdding}
                        hasCustomizationOptions={hasCustomizationOptions}
                        optionSelections={optionSelections}
                        showNotifyMeFormDialog={showNotifyMeFormDialog}
                        setIsCustomizationsDialogOpen={setIsCustomizationsDialogOpen}
                        isValid={isValid}
                        errors={errors}
                      />
                    </div>
                  </div>
                </div>

                <section className={classes.description}>{productFullAccordion}</section>

                <PageBuilderAttributes
                  product={product}
                  optionSelections={optionSelections}
                />
              </section>
            </div>
            <MobileAddToCartSuccessTip
              addToCartQty={quantity}
              addToCartStatus={addToCartStatus}
            />
            <HasCustomizationOptionsDialog
              image={mediaGalleryEntries[0]}
              product={product}
              isCustomizationsDialogOpen={isCustomizationsDialogOpen}
              setIsCustomizationsDialogOpen={setIsCustomizationsDialogOpen}
              onSubmit={onSubmit}
            />
          </form>
        </Form>

        <NotifyMeDialogLazyLoad
          productId={product.id}
          productSku={product.sku}
          variantSku={selectedProduct?.sku}
          onOpenChange={(nextOpen: boolean) => {
            nextOpen ? showNotifyMeFormDialog() : hiddenNotifyMeFormDialog();
          }}
          open={notifyMeFormDialogIsOpen}
          productOptions={product.configurable_options}
          optionSelections={optionSelections}
        />

        <SwatchAddToCartDialogLazyLoad
          open={swatchAddToCartDialogVisible}
          onOpenChange={(nextOpen: boolean) => {
            setSwatchAddToCartDialogVisible(nextOpen);
          }}
          swatchUrl={currentSwatchUrl}
        />

        <WaistTipDialog
          open={showWaistTipDialog}
          onCancel={handleWaistTipDialogCancel}
          onConfirm={handleWaistTipDialogConfirm}
        />

        <HeightTipDialog
          open={showHeightTipDialog}
          onCancel={handleHeightTipDialogCancel}
          onConfirm={handleHeightTipDialogConfirm}
        />
      </>
    );
  },
);

ProductFullDetailNew.displayName = 'ProductFullDetailNew';
